import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, X, Settings, RefreshCw, CheckCircle2, AlertTriangle, BrainCircuit, Globe, Sparkles, Printer, Eye, Check, Star } from 'lucide-react';
import { Can } from '../pos/Can';
import { ReceiptTemplateRenderer, RECEIPT_TEMPLATES, type ReceiptTemplateId, type ReceiptRendererData } from './ReceiptTemplateRenderer';
import { printReceiptImage } from '../../utils/thermalPrint';

// ─── Lazy-loaded MapPicker — separate async chunk avoids Rollup scope-hoisting crashes ──
const LazyMapPicker = React.lazy(() => import('../MapPicker'));

interface ProfileForm {
  name: string;
  phone: string;
  address: string;
  email: string;
  website: string;
  latitude: string;
  longitude: string;
}

interface SettingsViewProps {
  storeName: string;
  storeLogo: string | null;
  themePalette: { primaryBrand: string; secondaryAccent: string } | null;
  logoThemeEnabled: boolean;
  profileForm: ProfileForm;
  profileError: string | null;
  profileSaving: boolean;
  // Callbacks
  setStoreLogo: (logo: string | null) => void;
  setThemePalette: (palette: { primaryBrand: string; secondaryAccent: string } | null) => void;
  setLogoThemeEnabled: (enabled: boolean) => void;
  setProfileForm: (updater: (f: ProfileForm) => ProfileForm) => void;
  applyThemeColor: (palette: { primaryBrand: string; secondaryAccent: string } | null, enabled: boolean) => void;
  extractBrandPalette: (imageUrl: string) => Promise<{ primaryBrand: string; secondaryAccent: string } | null>;
  updateStoreProfile: () => void;
  navigateTo: (view: string) => void;
  paMode: boolean;
  paModeSaving: boolean;
  onTogglePaMode: () => void;
  // 🔒 Luna AI Privacy
  isLunaIndexed: boolean;
  onToggleLunaIndexed: () => void;
  // 🖨️ Show Kangavena logo on printed bills
  showLogoOnBill: boolean;
  onToggleShowLogoOnBill: () => void;
  // 🧾 Disable "Powered by Kangavena Cloud" footer from bill
  hidePoweredByOnBill?: boolean;
  onToggleHidePoweredByOnBill?: () => void;
  // ✨ Enable Luna Smart Upsell Suggestions
  enableLunaSuggestions?: boolean;
  onToggleEnableLunaSuggestions?: () => void;
  // 🧾 Receipt Template & Test Print
  receiptTemplate?: string;
  onSaveReceiptTemplate?: (templateId: string) => Promise<void> | void;
  storeApiKey?: string;
  showToast?: (msg: string, type?: 'success' | 'error' | 'info') => void;
}

export const SettingsView: React.FC<SettingsViewProps> = ({
  storeName,
  storeLogo,
  themePalette,
  logoThemeEnabled,
  profileForm,
  profileError,
  profileSaving,
  setStoreLogo,
  setThemePalette,
  setLogoThemeEnabled,
  setProfileForm,
  applyThemeColor,
  extractBrandPalette,
  updateStoreProfile,
  navigateTo,
  paMode,
  paModeSaving,
  onTogglePaMode,
  isLunaIndexed,
  onToggleLunaIndexed,
  showLogoOnBill,
  onToggleShowLogoOnBill,
  hidePoweredByOnBill = false,
  onToggleHidePoweredByOnBill,
  enableLunaSuggestions = true,
  onToggleEnableLunaSuggestions,
  receiptTemplate = 'modern_left_brand',
  onSaveReceiptTemplate,
  storeApiKey = '',
  showToast,
}) => {
  const [selectedTemplate, setSelectedTemplate] = React.useState<string>(receiptTemplate || 'modern_left_brand');
  const [isSavingTemplate, setIsSavingTemplate] = React.useState(false);
  const [isTestPrinting, setIsTestPrinting] = React.useState(false);

  React.useEffect(() => {
    if (receiptTemplate) {
      setSelectedTemplate(receiptTemplate);
    }
  }, [receiptTemplate]);

  const handleSetDefaultTemplate = async (templateId: string) => {
    setIsSavingTemplate(true);
    try {
      if (onSaveReceiptTemplate) {
        await onSaveReceiptTemplate(templateId);
      }
      showToast?.('Receipt template saved as permanent default!', 'success');
    } catch {
      showToast?.('Failed to save default template', 'error');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleTestPrint = async () => {
    setIsTestPrinting(true);
    try {
      const res = await printReceiptImage('kangavena-settings-test-print', storeApiKey);
      if (res.success) {
        showToast?.(`🖨️ Test print dispatched successfully to ${res.printer || 'Xprinter XP-470B'}!`, 'success');
      } else {
        showToast?.(`🖨️ ${res.error || 'Print failed — ensure Print Agent is running'}`, 'error');
      }
    } catch (e: any) {
      showToast?.(`🖨️ Print error: ${e.message}`, 'error');
    } finally {
      setIsTestPrinting(false);
    }
  };

  const sampleReceiptData: ReceiptRendererData = {
    id: 'TX_1790686179489_2y8r5',
    storeName: storeName || 'Induni Enterprise',
    storeLogo: storeLogo || null,
    storeAddress: profileForm.address || '62/4, kurudu waththa, kandy road, thihariya, Kalagedihena, Nittambuwa, Gampaha',
    storePhone: profileForm.phone || '0716542903',
    time: new Date(),
    cashierName: 'Manager',
    customer: {
      name: 'Walk-in Customer',
      phone: '0771234567',
      address: 'Kalagedihena'
    },
    cart: [
      {
        name: 'Hadalaight NKR Bumper',
        qty: 1,
        price: 35000,
        warrantyPeriod: '6 Months',
        returnPolicy: 'No return if installed'
      },
      {
        name: 'canter dash board geyer 2Ton',
        qty: 1,
        price: 75000,
        partNumber: 'DB-2T-88'
      },
      {
        name: 'Tube light 4light NKR',
        qty: 2,
        price: 100
      }
    ],
    subtotal: 110200,
    total: 110200,
    payments: [
      { method: 'cash', amount: 110200 }
    ],
    change: 0
  };

  return (
    <div className="fixed inset-0 z-[50] bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-sky-100 bg-white shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigateTo('pos')}
            className="w-9 h-9 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 transition-colors shrink-0"
            aria-label="Back"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="w-9 h-9 rounded-xl bg-sky-100 border border-sky-200 flex items-center justify-center shrink-0">
            <Settings size={16} className="text-sky-600" />
          </div>
          <div>
            <h2 className="text-[15px] font-bold text-slate-800 tracking-tight">Store Profile</h2>
            <p className="text-[9px] text-slate-500 font-medium">{storeName} · Public Information</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 max-w-2xl mx-auto w-full">

        {/* Store Name */}
        <div>
          <label className="block text-[9px] text-slate-500 uppercase tracking-widest font-mono mb-1.5">Store Name</label>
          <input type="text" value={profileForm.name}
            onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Tech Hub Ratnapura"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-[12px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100" />
        </div>

        {/* Store Logo Upload */}
        <div>
          <label className="block text-[9px] text-slate-500 uppercase tracking-widest font-mono mb-1.5">Store Logo</label>
          <div className="flex items-center gap-3">
            {storeLogo ? (
              <div className="relative">
                <img src={storeLogo} alt="Store Logo" className="h-16 w-16 object-contain rounded-lg border border-slate-200 bg-white p-1" />
                <button
                  onClick={() => {
                    setStoreLogo(null);
                    setThemePalette(null);
                    applyThemeColor(null, logoThemeEnabled);
                  }}
                  className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-rose-100 border border-rose-200 flex items-center justify-center text-rose-500 hover:bg-rose-200 transition-colors"
                >
                  <X size={10} />
                </button>
              </div>
            ) : (
              <div className="h-16 w-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50">
                <span className="text-slate-400 text-[10px]">No logo</span>
              </div>
            )}
            <div className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = async (ev) => {
                      const imageUrl = ev.target?.result as string;
                      setStoreLogo(imageUrl);
                      // 🎨 Extract full brand palette using Gemini Vision
                      if (logoThemeEnabled) {
                        const palette = await extractBrandPalette(imageUrl);
                        if (palette) {
                          setThemePalette(palette);
                          applyThemeColor(palette, true);
                        }
                      }
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                className="block w-full text-[10px] text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-sky-50 file:text-sky-600 file:text-[10px] file:font-semibold hover:file:bg-sky-100"
              />
              <p className="text-[8px] text-slate-400 mt-1">Upload a logo to display on receipts. Max 500KB.</p>
            </div>
          </div>

          {/* 🎨 Dynamic Theme Toggle */}
          <div className="mt-3 flex items-center justify-between p-2.5 rounded-xl bg-slate-50 border border-slate-200">
            <div className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full border border-slate-300"
                style={{
                  backgroundColor: themePalette
                    ? `rgb(${themePalette.primaryBrand})`
                    : '#0ea5e9'
                }}
              />
              <span className="text-[10px] text-slate-600 font-medium">Match theme to logo</span>
            </div>
            <button
              onClick={() => {
                const newEnabled = !logoThemeEnabled;
                setLogoThemeEnabled(newEnabled);
                applyThemeColor(themePalette, newEnabled);
                // If enabling and we have a logo but no palette, extract it
                if (newEnabled && storeLogo && !themePalette) {
                  extractBrandPalette(storeLogo).then(palette => {
                    if (palette) {
                      setThemePalette(palette);
                      applyThemeColor(palette, true);
                    }
                  });
                }
              }}
              className={`relative w-10 h-5 rounded-full transition-colors ${
                logoThemeEnabled ? 'bg-brand-primary' : 'bg-slate-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  logoThemeEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          {themePalette && logoThemeEnabled && (
            <p className="text-[8px] text-slate-400 mt-1">
              Primary: rgb({themePalette.primaryBrand}) · Secondary: rgb({themePalette.secondaryAccent})
            </p>
          )}
        </div>

        {/* Public Phone (customers) */}
        <div>
          <label className="block text-[9px] text-slate-500 uppercase tracking-widest font-mono mb-1.5">Public Phone (for customers)</label>
          <input type="tel" value={profileForm.phone}
            onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))}
            placeholder="e.g. +94 77 123 4567"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-[12px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100" />
        </div>


        {/* Full Address */}
        <div>
          <label className="block text-[9px] text-slate-500 uppercase tracking-widest font-mono mb-1.5">Full Address</label>
          <textarea value={profileForm.address} rows={2}
            onChange={e => setProfileForm(f => ({ ...f, address: e.target.value }))}
            placeholder="e.g. No. 42, Main Street, Ratnapura 70000"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-[12px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 resize-none" />
        </div>

        {/* Map Picker */}
        <div>
          <label className="block text-[9px] text-slate-500 uppercase tracking-widest font-mono mb-1">Store Location Map</label>
          <p className="text-[8px] text-slate-600 mb-1">Type your address above to auto-pin, or click the map to place manually. Drag the pin to fine-tune.</p>
          <React.Suspense fallback={<div className="h-52 w-full rounded-xl bg-slate-800/30 border border-purple-500/20 mt-2 flex items-center justify-center"><span className="text-xs text-slate-500 font-mono">Loading map…</span></div>}>
            <LazyMapPicker
              lat={profileForm.latitude}
              lng={profileForm.longitude}
              address={profileForm.address}
              onChange={(lat, lng) => setProfileForm(f => ({ ...f, latitude: lat, longitude: lng }))}
            />
          </React.Suspense>
        </div>

        {/* Email */}
        <div>
          <label className="block text-[9px] text-slate-500 uppercase tracking-widest font-mono mb-1.5">Email</label>
          <input type="email" value={profileForm.email}
            onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))}
            placeholder="e.g. techhub@gmail.com"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-[12px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100" />
        </div>

        {/* Website */}
        <div>
          <label className="block text-[9px] text-slate-500 uppercase tracking-widest font-mono mb-1.5">Website</label>
          <input type="url" value={profileForm.website}
            onChange={e => setProfileForm(f => ({ ...f, website: e.target.value }))}
            placeholder="e.g. https://techhub.lk"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-[12px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100" />
        </div>

        {/* Personal Assistant Mode */}
        <div className="bg-white border border-sky-100 rounded-xl p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <BrainCircuit size={16} className="text-sky-600" />
                <span className="text-[11px] font-bold text-slate-700">Personal Assistant Mode</span>
              </div>
              <p className="text-[9px] text-slate-500 leading-relaxed">
                When enabled, Luna acts as a general-purpose personal assistant without access to store inventory, sales, or employee data. Use this if you want help with general tasks rather than store operations.
              </p>
            </div>
            <button
              onClick={onTogglePaMode}
              disabled={paModeSaving}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
                paMode ? 'bg-sky-500' : 'bg-slate-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  paMode ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <p className="text-[8px] font-mono mt-2 text-slate-400">
            Current: {paMode ? 'UNRESTRICTED — no store data access' : 'STORE MODE — full POS access'}
          </p>
        </div>

        {/* 🔒 Luna AI Public Visibility */}
        <div className="bg-white border border-sky-100 rounded-xl p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Globe size={16} className="text-emerald-600" />
                <span className="text-[11px] font-bold text-slate-700">List Store on Luna AI Public Search</span>
              </div>
              <p className="text-[9px] text-slate-500 leading-relaxed">
                When enabled, your store and products appear in Luna's public search results. Disable to completely hide your store from public AI discovery.
              </p>
            </div>
            <button
              onClick={onToggleLunaIndexed}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
                isLunaIndexed ? 'bg-emerald-500' : 'bg-slate-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  isLunaIndexed ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <p className="text-[8px] font-mono mt-2 text-slate-400">
            Current: {isLunaIndexed ? 'VISIBLE — store appears in Luna public search' : 'HIDDEN — store excluded from Luna public search'}
          </p>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            🧾 RECEIPT TEMPLATES & LIVE TEST PRINT STUDIO (10 TEMPLATES)
            ══════════════════════════════════════════════════════════════════ */}
        <div className="bg-white border border-sky-200/80 rounded-2xl p-5 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[20px]">🧾</span>
                <h3 className="text-[13px] font-black uppercase tracking-wider text-slate-800">
                  Receipt Templates & Print Layout (තාප මුද්‍රණ රිසිට්පත් සැලසුම්)
                </h3>
              </div>
              <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
                Choose from 10 distinct, thermal-optimized receipt layouts. Selected default will be permanently used for all customer prints. Preview live and send an instant test print directly to your Xprinter XP-470B.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1">
                <Check size={11} /> 100% WYSIWYG
              </span>
              <span className="px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider bg-sky-100 text-sky-800 border border-sky-300">
                4-Inch XP-470B & 80mm
              </span>
            </div>
          </div>

          {/* Grid Layout: Template Cards on Left, Live Preview + Test Print on Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* Left Col: 10 Template Cards (7 cols) */}
            <div className="lg:col-span-7 space-y-2.5 max-h-[560px] overflow-y-auto pr-1">
              {RECEIPT_TEMPLATES.map((tmpl) => {
                const isSelected = selectedTemplate === tmpl.id;
                const isCurrentDefault = (receiptTemplate || 'modern_left_brand') === tmpl.id;

                return (
                  <div
                    key={tmpl.id}
                    onClick={() => setSelectedTemplate(tmpl.id)}
                    className={`p-3 rounded-xl border cursor-pointer transition-all flex items-start gap-3 relative ${
                      isSelected
                        ? 'border-sky-500 bg-sky-50/70 shadow-sm ring-2 ring-sky-400/30'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50'
                    }`}
                  >
                    <div className="text-2xl shrink-0 p-2 rounded-lg bg-white border border-slate-200/80 shadow-xs">
                      {tmpl.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-black text-slate-800">
                          {tmpl.name}
                        </span>
                        <span className="text-[8px] font-bold text-slate-400">
                          ({tmpl.nameSi})
                        </span>
                        {tmpl.tag && (
                          <span className="px-1.5 py-0.5 rounded text-[7.5px] font-black uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">
                            {tmpl.tag}
                          </span>
                        )}
                        {isCurrentDefault && (
                          <span className="px-1.5 py-0.5 rounded text-[7.5px] font-black uppercase tracking-wider bg-emerald-600 text-white flex items-center gap-0.5 shadow-xs">
                            <Star size={9} fill="white" /> CURRENT DEFAULT
                          </span>
                        )}
                      </div>
                      <p className="text-[9.5px] text-slate-500 mt-1 leading-snug">
                        {tmpl.description}
                      </p>
                    </div>

                    <div className="shrink-0 flex items-center gap-1 self-center">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTemplate(tmpl.id);
                          handleSetDefaultTemplate(tmpl.id);
                        }}
                        disabled={isSavingTemplate || isCurrentDefault}
                        className={`px-2.5 py-1.5 rounded-lg text-[9px] font-bold tracking-wider uppercase transition-all ${
                          isCurrentDefault
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default'
                            : 'bg-slate-900 text-white hover:bg-sky-600 active:scale-95'
                        }`}
                      >
                        {isCurrentDefault ? 'Active Default' : 'Set Default'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right Col: Live Interactive Receipt Preview & Test Print Trigger (5 cols) */}
            <div className="lg:col-span-5 flex flex-col items-center space-y-3 bg-slate-100/70 p-4 rounded-xl border border-slate-200">
              <div className="w-full flex items-center justify-between">
                <span className="text-[10px] font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Eye size={13} className="text-sky-600" /> Live Receipt Preview (100% WYSIWYG)
                </span>
                <span className="text-[8px] font-mono text-slate-400">
                  XP-470B 4-Inch Scale
                </span>
              </div>

              {/* The Receipt Preview Container (with id for capture test print) */}
              <div className="w-full max-w-[340px] bg-white rounded-lg shadow-md border border-slate-300 overflow-hidden">
                <ReceiptTemplateRenderer
                  id="kangavena-settings-test-print"
                  templateId={selectedTemplate}
                  data={sampleReceiptData}
                  is4Inch={true}
                  showLogoOnBill={showLogoOnBill}
                  hidePoweredByOnBill={hidePoweredByOnBill}
                />
              </div>

              {/* Control Action Buttons */}
              <div className="w-full space-y-2 pt-1">
                <button
                  type="button"
                  onClick={handleTestPrint}
                  disabled={isTestPrinting}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-black text-[11px] uppercase tracking-wider bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-500 hover:to-cyan-500 text-white shadow-md active:scale-95 transition-all disabled:opacity-50 cursor-pointer"
                >
                  {isTestPrinting ? (
                    <>
                      <RefreshCw size={13} className="animate-spin" />
                      Dispatching to Printer...
                    </>
                  ) : (
                    <>
                      <Printer size={14} />
                      🖨️ Test Print This Template (දැන්ම Print කර බලන්න)
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => handleSetDefaultTemplate(selectedTemplate)}
                  disabled={isSavingTemplate || (receiptTemplate || 'modern_left_brand') === selectedTemplate}
                  className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-xl font-bold text-[10px] uppercase tracking-wider bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 transition-all disabled:opacity-40"
                >
                  {(receiptTemplate || 'modern_left_brand') === selectedTemplate ? (
                    <>
                      <CheckCircle2 size={13} className="text-emerald-600" />
                      Currently Set as Permanent Default
                    </>
                  ) : (
                    <>
                      <Star size={13} className="text-amber-500" />
                      Save "{RECEIPT_TEMPLATES.find(t => t.id === selectedTemplate)?.name}" as Default
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 🖨️ Show Kangavena Logo on Bill */}
        <div className="bg-white border border-sky-100 rounded-xl p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[16px]">🧾</span>
                <span className="text-[11px] font-bold text-slate-700">Show System Logo on Bill</span>
              </div>
              <p className="text-[9px] text-slate-500 leading-relaxed">
                When enabled, the Kangavena system logo prints on receipts above your store name. Disable to show only your store name on printed bills.
              </p>
            </div>
            <button
              onClick={onToggleShowLogoOnBill}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
                showLogoOnBill ? 'bg-sky-500' : 'bg-slate-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  showLogoOnBill ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <p className="text-[8px] font-mono mt-2 text-slate-400">
            Current: {showLogoOnBill ? 'ON — Kangavena logo prints on receipts' : 'OFF — only store name prints'}
          </p>
        </div>

        {/* 🧾 Disable "Powered by Kangavena" Attribution */}
        <div className="bg-white border border-sky-100 rounded-xl p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[16px]">🏷️</span>
                <span className="text-[11px] font-bold text-slate-700">Disable "Powered by Kangavena" on Bill</span>
              </div>
              <p className="text-[9px] text-slate-500 leading-relaxed">
                When enabled, hides the "Powered by Kangavena Cloud" footer from printed receipts and customer bills for completely clean white-label receipts.
              </p>
            </div>
            <button
              onClick={onToggleHidePoweredByOnBill}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
                hidePoweredByOnBill ? 'bg-sky-500' : 'bg-slate-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  hidePoweredByOnBill ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <p className="text-[8px] font-mono mt-2 text-slate-400">
            Current: {hidePoweredByOnBill ? 'DISABLED — "Powered by Kangavena" is hidden from bills' : 'ENABLED — "Powered by Kangavena" prints on footer'}
          </p>
        </div>

        {/* ✨ Luna Smart Upsell Suggestions */}
        <div className="bg-white border border-sky-100 rounded-xl p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={16} className="text-amber-500" />
                <span className="text-[11px] font-bold text-slate-700">Luna Smart Upsell Suggestions</span>
              </div>
              <p className="text-[9px] text-slate-500 leading-relaxed">
                When enabled, Luna analyzes current cart items and intelligently suggests relevant in-stock cross-sell recommendations from your store inventory. Disable to turn off all cart suggestions completely. (Luna AI යෝජනා සක්‍රීය/අක්‍රීය කරන්න)
              </p>
            </div>
            <button
              onClick={onToggleEnableLunaSuggestions}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
                enableLunaSuggestions ? 'bg-amber-500' : 'bg-slate-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  enableLunaSuggestions ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <p className="text-[8px] font-mono mt-2 text-slate-400">
            Current: {enableLunaSuggestions ? 'ENABLED — smart upsell suggestions active' : 'DISABLED — cart suggestions hidden'}
          </p>
        </div>

        <AnimatePresence>
          {profileError && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              className="flex items-center gap-2 text-[11px] text-rose-600 px-3 py-2 rounded-xl bg-rose-50 border border-rose-200">
              <AlertTriangle size={12} /> {profileError}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer */}
        <div className="relative z-10 px-6 pb-5">
          <button onClick={updateStoreProfile} disabled={profileSaving}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-black text-[11px] uppercase tracking-widest transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)', color: '#000', boxShadow: '0 0 24px rgba(6,182,212,0.2)' }}>
            {profileSaving ? <><RefreshCw size={13} className="animate-spin" /> Saving…</> : <><CheckCircle2 size={13} /> Save Profile</>}
          </button>
        </div>
      </div>
    </div>
  );
};
